import * as cn from "./Final.scss"

import * as React from "react"
import {Map} from "immutable"

import {Demand as DemandModel, DemandList, fromJs as demandFromJs, JsonData as DemandJsonData} from "./../model/Demand"
import {Division, Divisions, fromJs as divisionFromJs, JsonData as DivisionJsonData} from "./../model/Division"
import {UniversityBranches, fromJs as universityBranchFromJs, JsonData as UniversityBranchJsonData} from "./../model/UniversityBranch"
import {Speciality, Specialities, fromJs as specialityFromJs, JsonData as SpecialityJsonData} from "./../model/Speciality"
import {User, fromJs as userFromJs, JsonData as UserJsonData} from "./../model/User"
import {Summary} from "./Summary"
import {Demand, Type} from "./Demand"
import {TraineeDemand} from "./TraineeDemand"
import {Entrance} from "./Entrance"
import {Opk} from "./Opk"
import {
    DEMAND_YEARS,
    EMPLOYEE_YEAR, employeeYearToColumn,
    ENTRANCE_YEARS,
    FINANCE_YEARS,
    TRAINEE_DEMAND_YEARS,
    VACANCY_YEARS,
    CURRENT_YEAR,
    IS_SUMMER,
} from "../Demand/common";
import {logger} from "../utils/logger";
import {Vacancy} from "../model/Vacancy";
import {Employee, Fired} from "../model/Employee";
import {Preloader} from "../React/Preloader";
import {Finance} from "../model/Finance";
import {Trainee} from "../model/Trainee";
import {FinanceReport} from "./FinanceReport";

interface PartialState {
    loaded?: boolean
    demand?: DemandList
    divisions?: Divisions
    specialities?: Specialities
    universityBranches?: UniversityBranches,
    finances?: Finance[],
    meta?: {
        company: string
        user: User
    }
}

interface State extends PartialState {
    loaded: boolean
}

const isSummer = IS_SUMMER

type employeeYear = 'employee25'
type employeeAvg = 'employee25Avg'
export const employeeYear: employeeYear = 'employee25'
export const employeeAvg: employeeAvg = 'employee25Avg' as const

export const yearColumns = VACANCY_YEARS.concat(DEMAND_YEARS);
export const columns = [employeeYear, employeeAvg, ...yearColumns]
export const entranceColumns = ENTRANCE_YEARS.filter(year => year > CURRENT_YEAR);
export const opkColumns = ENTRANCE_YEARS.filter(year => year > CURRENT_YEAR);
export const financeColumns = FINANCE_YEARS;
export const traineeDemandColumns = TRAINEE_DEMAND_YEARS;

export type SummaryRow = {
    [employeeYear]: number
    [employeeAvg]: number
    2016: number
    2017: number
    2018: number
    2019: number
    2020: number
    2021: number
    2022: number
    2023: number
    2024: number
    2025: number
    2026: number
    2027: number
    2028: number
    2029: number
    2030: number
    2031: number
    2032: number
    2033: number
}
export type OpkSummaryRow = {
    2018: number
    2019: number
    2020: number
    2021: number
}

export type FinanceSummaryRow = {
    final: number
    forecast: number
}

export type SummaryMatrix = {
    demandVo: SummaryRow
    demandSpo: SummaryRow
    demandPhD: SummaryRow
    demandNuclear: SummaryRow
    demandTotal: SummaryRow
    demandAesProjects: SummaryRow
    demandNationalProject: SummaryRow
    traineeDemandVo: SummaryRow
    traineeDemandSpo: SummaryRow
    traineeDemandPhD: SummaryRow
    traineeDemandNuclear: SummaryRow
    traineeDemandTotal: SummaryRow
    traineeDemandAesProjects: SummaryRow
    targetVo: SummaryRow
    targetSpo: SummaryRow
    targetPhD: SummaryRow
    hiPoVo: SummaryRow
    hiPoSpo: SummaryRow
    hiPoPhD: SummaryRow
    entranceVo: SummaryRow
    entranceSpo: SummaryRow
    entrancePhD: SummaryRow
    entranceTotal: SummaryRow
    entranceNuclear: SummaryRow
    entranceNationalProject: SummaryRow
    opkTotal: OpkSummaryRow
    opkTotalOpk: OpkSummaryRow
    opkTotalNotOpk: OpkSummaryRow
    financeTotal: FinanceSummaryRow
}

type Response = {
    demand: DemandJsonData[]
    universityBranches: UniversityBranchJsonData[]
    divisions: DivisionJsonData[]
    specialities: SpecialityJsonData[]
    finances: Finance[]
    meta: {
        company: string
        user: UserJsonData
    }
}

let opkCache: {[key: number]: boolean} = {}

export const companyIsOpk = (divisions: Divisions, id): boolean => {
    if (opkCache[id] === undefined) {
        divisions.map(({companies}: Division) => {
            if (companies.has(id)) {
                opkCache[id] = companies.get(id).isOpk
            }
        })
    }

    return opkCache[id]
}

export class Final extends React.PureComponent<{}, PartialState> {
    debugRenderPerformance = true
    displayName = __filename
    state: State = {
        loaded: false,
    }

    componentDidMount() {
        fetch("/export/prior", {credentials: "include"})
   //     fetch("http://127.0.0.1:8080/js/325.json")
            .then(response => response.json())
            .then<Response>(response => response as any)
            .then(({demand, divisions, specialities: rawSpecialities, universityBranches, meta, finances}: Response) => {
                const specialities: Specialities = Map(rawSpecialities.map(speciality => [speciality.id, specialityFromJs(speciality)]));

                this.setState({
                    loaded: true,
                    demand:             Map(demand.map(demand => [demand.id, demandFromJs(demand, specialities)])) as DemandList,
                    divisions:          Map(divisions.map(division => [division.id, divisionFromJs(division)])) as Divisions,
                    specialities,
                    finances,
                    universityBranches: Map(universityBranches.map(universityBranch => [universityBranch.id, universityBranchFromJs(universityBranch)])) as UniversityBranches,
                    meta: {
                        company: meta.company,
                        user: userFromJs(meta.user)
                    },
                })
            })
            .catch(error => logger.captureException(error))
    }

    render() {
        if (!this.state.loaded) {
            return <Preloader/>
        }

        const {
            demand,
            divisions,
            specialities,
            universityBranches,
            finances,
            meta,
        } = this.state

        try {
            if (universityBranches === undefined) {
                throw new Error("UniversityBranches are empty for Preloader.tsx. Possibly rendering before fetching data")
            }

            if (specialities === undefined) {
                throw new Error("Specialities are empty for Preloader.tsx. Possibly rendering before fetching data")
            }

            if (divisions === undefined) {
                throw new Error("Divisions are empty for Preloader.tsx. Possibly rendering before fetching data")
            }

            if (demand === undefined) {
                throw new Error("Demand is empty for Preloader.tsx. Possibly rendering before fetching data")
            }

            if (finances === undefined) {
                throw new Error("Demand is empty for Preloader.tsx. Possibly rendering before fetching data")
            }

            if (meta === undefined) {
                throw new Error("User info is empty for Preloader.tsx. Possibly rendering before fetching data")
            }
        } catch (e) {
            logger.captureException(e)

            return <span/>
        }

        const {
            company,
            user,
        } = meta

        let summary: SummaryMatrix = {} as SummaryMatrix

        ;([
            "demandVo", "demandSpo", "demandPhD", "demandNuclear", "demandTotal", "demandAesProjects", "demandNationalProject",
            "traineeDemandVo", "traineeDemandSpo", "traineeDemandPhD", "traineeDemandNuclear", "traineeDemandTotal", "traineeDemandAesProjects",
            "targetVo", "targetSpo", "targetPhD", "hiPoVo", "hiPoSpo", "hiPoPhD",
            "entranceVo", "entranceSpo", "entrancePhD", "entranceTotal", "entranceNuclear", "entranceNationalProject"
        ]).map(row => {
            summary[row] = {}
            columns.map(column => (summary[row][column] = 0))
        })

        ;([
            "opkTotal",
            "opkTotalOpk",
            "opkTotalNotOpk",
        ]).map(row => {
            summary[row] = {}
            opkColumns.map(column => (summary[row][column] = 0))
        })

        ;([
            "financeTotal",
        ]).map(row => {
            summary[row] = {
                final: 0,
                forecast: 0,
            }
        })

        demand.map(({
            specialityId,
            vacancies,
            employees,
            entranceByYear,
            demandByYear,
            targetByYear,
            traineeDemandByYear,
//            universityBranchId,
            targetRequestByYear,
            companyId,
            businessType,
            addBachelorTarget,
            targetRequestTotal,
            isPracticePaid,
            students,
            trainees,
            nationalProject,
        }: DemandModel) => {
            const speciality: Speciality = specialities.get(specialityId)

            if (speciality) {
                let suffix: "Vo" | "Spo" | "PhD"

                if (speciality.isVo()) {
                    suffix = "Vo"
                } else if (speciality.isSpo()) {
                    suffix = "Spo"
                } else if (speciality.isPhD()) {
                    suffix = "PhD"
                } else {
                    logger.captureException(`Speciality[${specialityId}] has unknown type`)

                    suffix = "Vo"
                }

                const employeesFiltered = employees.filter((employee: Employee) => employee.demandYear === EMPLOYEE_YEAR && employee.fired !== Fired.Fired);
                const traineeCurrentYearCount = isPracticePaid
                    ? trainees.filter((student: Trainee) => student.demandYear === EMPLOYEE_YEAR).reduce((r: number, {demand}: Trainee) => r + (demand || 0), 0)
                    : 0;

                if (employeesFiltered.size) {
                    summary[`demand${suffix}`][employeeYear] += employeesFiltered.size
                    summary[`demand${suffix}`][employeeAvg] += employeesFiltered.reduce((r: number, {gpa}: Employee) => r + (gpa || 0), 0)

                    employeesFiltered.filter(({hiPo}: Employee) => hiPo).map(({gpa}: Employee) => {
                        summary[`hiPo${suffix}`][employeeYear]++
                        summary[`hiPo${suffix}`][employeeAvg] += gpa || 0
                    })

                    const targetEmployees = employeesFiltered.filter(({target}: Employee) => target)

                    if (targetEmployees.size) {
                        summary[`target${suffix}`][employeeYear] += targetEmployees.size
                        summary[`target${suffix}`][employeeAvg] += targetEmployees.reduce((r: number, {gpa}: Employee) => r + (gpa || 0), 0)
                    }
                }

                yearColumns.map(year => {
                    summary[`demand${suffix}`][year] += (demandByYear[year] || 0)
                    summary[`target${suffix}`][year] += (targetByYear[year] || 0)
                    if (isPracticePaid) {
                        summary[`traineeDemand${suffix}`][year] += (traineeDemandByYear[year] || 0)
                    }
                })

                vacancies.map(({year, hiPo}: Vacancy) => {
                    summary[`hiPo${suffix}`][year] += hiPo
                })

                entranceColumns.map(year => {
                    summary[`entrance${suffix}`][year] += (entranceByYear[year] || 0)

                    if (speciality.isNuclear()) {
                        summary.entranceNuclear[year] += (entranceByYear[year] || 0)
                    }

                    // National project entrance calculation
                    if (nationalProject && nationalProject !== "none") {
                        summary.entranceNationalProject[year] += (entranceByYear[year] || 0)
                    }
                })

                const bachelorSpecialityId = speciality.bachelorSpecialityId

                if (speciality.isMaster() && bachelorSpecialityId) {
                    entranceColumns.map(year => {
                        summary[`entrance${suffix}`][year] += (entranceByYear[year + 4] || 0)

                        const bachelorSpeciality = specialities.get(bachelorSpecialityId)

                        if (bachelorSpeciality && bachelorSpeciality.isNuclear()) {
                            summary.entranceNuclear[year] += (entranceByYear[year + 4] || 0)
                        }

                        // National project bachelor speciality calculation
                        if (nationalProject && nationalProject !== "none") {
                            summary.entranceNationalProject[year] += (entranceByYear[year + 4] || 0)
                        }
                    })
                }

                opkColumns.map(year => {
                    summary.opkTotal[year] += (targetRequestByYear[year] || 0)
                })

                if (speciality.isMaster() && speciality.bachelorSpecialityId && addBachelorTarget && targetRequestTotal(opkColumns, 4)) {
                    opkColumns.map(year => {
                        summary.opkTotal[year] += (targetRequestByYear[year + 4] || 0)
                    })
                }

                if (companyIsOpk(divisions, companyId)/* && universityBranches.get(universityBranchId).specialitiesOpk.has(specialityId)*/) {
                    opkColumns.map(year => {
                        summary.opkTotalOpk[year] += (targetRequestByYear[year] || 0)
                    })

                    if (speciality.isMaster() && speciality.bachelorSpecialityId && addBachelorTarget && targetRequestTotal(opkColumns, 4)) {
                        opkColumns.map(year => {
                            summary.opkTotalOpk[year] += (targetRequestByYear[year + 4] || 0)
                        })
                    }
                } else {
                    opkColumns.map(year => {
                        summary.opkTotalNotOpk[year] += (targetRequestByYear[year] || 0)
                    })

                    if (speciality.isMaster() && speciality.bachelorSpecialityId && addBachelorTarget && targetRequestTotal(opkColumns, 4)) {
                        opkColumns.map(year => {
                            summary.opkTotalNotOpk[year] += (targetRequestByYear[year + 4] || 0)
                        })
                    }
                }

                if (isPracticePaid) {
                    summary[`traineeDemand${suffix}`][employeeYear] += traineeCurrentYearCount
                }

                if (speciality.isNuclear()) {
                    yearColumns.forEach(year => {
                        summary.demandNuclear[year] += (demandByYear[year] || 0)
                        if (isPracticePaid) {
                            summary.traineeDemandNuclear[year] += (traineeDemandByYear[year] || 0)
                        }
                    })

                    if (isPracticePaid) {
                        summary.traineeDemandNuclear[employeeYear] += traineeCurrentYearCount
                    }

                    if (employeesFiltered.size) {
                        summary.demandNuclear[employeeYear] += employeesFiltered.size
                        summary.demandNuclear[employeeAvg] += employeesFiltered.reduce((r: number, {gpa}: Employee) => r + (gpa || 0), 0)
                    }
                }

                if (businessType === 'aes_projects') {
                    DEMAND_YEARS.forEach(year => {
                        summary.demandAesProjects[year] += (demandByYear[year] || 0)
                        if (isPracticePaid) {
                            summary.traineeDemandAesProjects[employeeYear] += traineeCurrentYearCount
                            summary.traineeDemandAesProjects[year] += (traineeDemandByYear[year] || 0)
                        }
                    })
                }

                // National project calculation
                if (nationalProject && nationalProject !== "none") {
                    yearColumns.forEach(year => {
                        summary.demandNationalProject[year] += (demandByYear[year] || 0)
                    })
                    
                    if (employeesFiltered.size) {
                        summary.demandNationalProject[employeeYear] += employeesFiltered.size
                        summary.demandNationalProject[employeeAvg] += employeesFiltered.reduce((r: number, {gpa}: Employee) => r + (gpa || 0), 0)
                    }
                }

                vacancies.filter(() => businessType === 'aes_projects').forEach(({year, demand }: Vacancy) => {
                    summary.demandAesProjects[year] += demand
                })

                const employeesAes = employees.filter(() => businessType === 'aes_projects')

                if (employeesAes.size) {
                    summary.demandAesProjects[employeeYear] += employeesAes.size
                    summary.demandAesProjects[employeeAvg] += employeesAes.reduce((r: number, {gpa}: Employee) => r + (gpa || 0), 0)
                }
            }
        })

        finances.map(({
            yearFinal,
            yearForecast,
        }: Finance) => {
            summary.financeTotal.final += yearFinal
            summary.financeTotal.forecast += yearForecast
        })

        summary.demandTotal = columns.map(column => [column, summary.demandVo[column] + summary.demandSpo[column] + summary.demandPhD[column]]).reduce((o, [c, v]) => {
            o[c] = v
            return o;
        }, {} as SummaryRow)

        summary.traineeDemandTotal = columns.map(column => [column, summary.traineeDemandVo[column] + summary.traineeDemandSpo[column] + summary.traineeDemandPhD[column]]).reduce((o, [c, v]) => {
            o[c] = v
            return o;
        }, {} as SummaryRow)


        summary.entranceTotal = columns.map(column => [column, summary.entranceVo[column] + summary.entranceSpo[column] + summary.entrancePhD[column]]).reduce((o, [c, v]) => {
            o[c] = v
            return o;
        }, {} as SummaryRow)

        let order = 0
        return (
            <div className = {cn.component}>
                <Summary {...{summary, user, company}}/>
                <Demand {...{
                    demand,
                    divisions,
                    specialities,
                    universityBranches,
                    user,
                    company,
                    total: summary.demandTotal,
                    totalNuclear: summary.demandNuclear,
                    totalAesProjects: summary.demandAesProjects,
                    type: Type.Demand,
                    order: ++order,
                }}/>
                {isSummer && (
                    <Entrance {...{
                        demand,
                        divisions,
                        specialities,
                        universityBranches,
                        user,
                        company,
                        total: summary.entranceTotal,
                        totalNuclear: summary.entranceNuclear,
                        totalNationalProject: summary.entranceNationalProject,
                        order: ++order,
                    }}/>
                )}
                <Opk {...{
                    demand,
                    divisions,
                    specialities,
                    universityBranches,
                    user,
                    company,
                    total: summary.opkTotal,
                    totalOpk: summary.opkTotalOpk,
                    totalNotOpk: summary.opkTotalNotOpk,
                    order: ++order,
                }}/>
                <TraineeDemand {...{
                    demand,
                    divisions,
                    specialities,
                    universityBranches,
                    user,
                    company,
                    total: summary.traineeDemandTotal,
                    totalNuclear: summary.traineeDemandNuclear,
                    totalAesProjects: summary.traineeDemandAesProjects,
                    order: ++order,
                }}/>
                {!isSummer && (
                    <FinanceReport {...{
                        finances,
                        divisions,
                        universityBranches,
                        user,
                        company,
                        total: summary.financeTotal,
                        order: ++order,
                    }}/>
                )}
            </div>
        )
    }
}